import Error from "../components/core/Error";
import Loading from "../components/core/Loading";
import PresentationFormControllerNew from "../components/presentations/PresentationFormControllerNew";
import { Alert, Box } from "@mui/material";
import { ApplicationStatus } from "../interfaces/enums";
import { isRegistrationOpen } from "../utils/utils";
import { PresentationWithId } from "../interfaces/IPresentations";
import { useApplicationContext } from "../contexts/ApplicationContext";
import { useContestContext } from "../contexts/ContestContext";
import { useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useNavigationHelper } from "../hooks/useNavigationHelper";
import { usePresentation } from "../hooks/usePresentation";

export default function ApplicationPresentationEditNew() {
  const { presentationId } = useParams();
  const { application } = useApplicationContext();
  const { contest } = useContestContext();
  const { goBackToApplicationDetails } = useNavigationHelper();
  const { presentation, isLoading, updatePresentation } = usePresentation(presentationId as string);
  const location = useLocation();
  const savedState = location.state;
  
  const [showRegistrationClosedAlert, setShowRegistrationClosedAlert] = useState(false);
  const wasRegistrationOpenRef = useRef<boolean>(true); // Zakładamy, że rejestracja była otwarta na początku

  // Śledź zmiany statusu rejestracji - pokaż alert jeśli została zamknięta podczas pracy
  useEffect(() => {
    if (isLoading || !contest) return;
    
    const registrationOpen = isRegistrationOpen(contest);
    
    // Jeśli rejestracja była otwarta, a teraz jest zamknięta - pokaż alert
    if (wasRegistrationOpenRef.current && !registrationOpen) {
      setShowRegistrationClosedAlert(true);
    }
    
    // Zaktualizuj referencję
    wasRegistrationOpenRef.current = registrationOpen;
  }, [isLoading, contest]);

  const handleUpdate = async (updatedPresentation: PresentationWithId) => {
    // Sprawdź jeszcze raz przed zapisem
    if (!isRegistrationOpen(contest || undefined)) {
      setShowRegistrationClosedAlert(true);
      return;
    }

    const result = await updatePresentation(updatedPresentation.id!, updatedPresentation);
    if (result.success) {
      // Wróć do poprzedniej strony z zachowaniem state
      goBackToApplicationDetails(
        application?.contestId as string,
        application?.id as string,
        savedState,
      );
    } else {
      console.error("Błąd podczas aktualizacji prezentacji:", result.error);
    }
  };

  // Pokazuj loading podczas ładowania
  if (isLoading) {
    return <Loading />;
  }

  // Pokazuj błąd jeśli nie udało się załadować prezentacji
  if (!presentation) {
    return <Error text="Nie udało się załadować prezentacji" />;
  }

  const registrationOpen = isRegistrationOpen(contest || undefined);

  return (
    <>
      {showRegistrationClosedAlert && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="warning" sx={{ border: "1px solid", borderColor: "warning.main", borderRadius: 1 }}>
            W trakcie edycji prezentacji rejestracja na {contest?.name || "turniej"} została zakończona. Nie można edytować prezentacji.
          </Alert>
        </Box>
      )}
      <PresentationFormControllerNew
        editableMode={application?.status === ApplicationStatus.DRAFT && registrationOpen}
        initialData={presentation}
        onSubmit={handleUpdate}
        onCancel={() =>
          goBackToApplicationDetails(
            application?.contestId as string,
            application?.id as string,
            savedState,
          )
        }
        registrationBlocked={!registrationOpen}
      />
    </>
  );
}
